<template>
  <div id="upload-slip-page">
    <v-container>
      <v-alert
        v-model="alert"
        type="success"
        color="primary"
        transition="slide-y-transition"
        dismissible
      >
        คุณได้ทำการสั่งซื้อสำเร็จแล้ว
      </v-alert>
      <v-card class="pa-10 mb-8">
        <div class="_fs-36">
          แจ้งหลักฐานการชำระเงิน
        </div>
        <div>กรอกข้อมูลการโอนเงินของท่าน พร้อมแนบรูปหลักฐาน</div>

        <v-divider class="mt-4 mb-8" />

        <validation-observer ref="uploadSlipForm">
          <base-text-field
            v-if="isUploadNewLineOfficialAccount"
            label="เลขที่เอกสาร"
            :value="order.code"
            readonly
          >
          </base-text-field>
          <div v-else>
            <validation-provider
              v-slot="{ errors }"
              ref="selectOrderId"
              rules="required"
              name="เลขที่เอกสาร"
            >
              <v-select
                v-model="selectOrderId"
                label="เลขที่เอกสาร"
                filled
                rounded
                :items="mapOrders"
                :error-messages="errors"
                @change="selectOrder"
              />
            </validation-provider>
          </div>

          <validation-provider
            v-slot="{ errors }"
            ref="paidAmount"
            rules="required|decimals:2"
            name="จำนวนเงินที่โอน"
          >
            <base-text-field
              v-model="paidAmount"
              :value="paidAmount"
              inputmode="decimal"
              label="จำนวนเงินที่โอน"
              type="text"
              hint="กรุณาชำระเงินตามยอดในใบเสนอราคาเท่านั้น หากชำระเงินไม่ครบถ้วน
              จะไม่สามารถเปิดให้บริการได้"
              persistent-hint
              readonly
              :error-messages="errors"
              class="HintTextColor" />
          </validation-provider>

          <v-divider class="my-8" />

          <div class="_fs-24 mb-4">
            วิธีการชำระเงิน
          </div>

          <validation-provider
            v-slot="{ errors }"
            ref="paymentMethod"
            rules="required"
            name="วิธีการชำระเงิน"
          >
            <v-radio-group
              :value="paymentMethod"
              row
              :disabled="!selectOrderId"
              :error-messages="$route.params.payment ? errors : []">
              <v-row>
                <v-col
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-radio
                    value="qrCode"
                    @click="() => handleChangePaymentMethod('qrCode')"
                  >
                    <template v-slot:label>
                      <div>
                        <v-icon>mdi-qrcode</v-icon>
                        QR Code
                      </div>
                    </template>
                  </v-radio>
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-radio
                    value="transfer"
                    @click="() => handleChangePaymentMethod('transfer')"
                  >
                    <template v-slot:label>
                      <div>
                        <v-icon>mdi-cash-multiple</v-icon>
                        โอน/ชำระผ่านบัญชีธนาคาร
                      </div>
                    </template>
                  </v-radio>
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-radio
                    value="byCreditCard"
                    :disabled="!canPaidByCreditCard"
                    @click="() => handleChangePaymentMethod('byCreditCard')"
                  >
                    <template v-slot:label>
                      <div>
                        <v-icon>mdi-credit-card-outline</v-icon>
                        บัตรเครดิต/บัตรเดบิต
                      </div>
                    </template>
                  </v-radio>
                  <p
                    v-if="formReady && !canPaidByCreditCard"
                    class="all-rights-reserved">
                    ขอสงวนสิทธิ์การจ่ายบัตรเครดิต เมื่อมียอดสั่งซื้อขั้นต่ำ 1,000 บาท
                  </p>
                </v-col>
              </v-row>
            </v-radio-group>
          </validation-provider>

          <v-card
            tile
            :elevation="0"
            class="my-6"
          >
            <v-row justify="center">
              <v-col cols="12">
                <PaymentMethod
                  v-if="order"
                  :order-result="order"
                  :select-payment-method="paymentMethod"
                  @changePayment="changePaymentMethod" />
              </v-col>
            </v-row>
          </v-card>

          <template v-if="isTransferToBankAccount">
            <validation-provider
              v-slot="{ errors }"
              ref="paidDate"
              rules="required"
              name="วันที่โอนเงิน">
              <base-date-picker
                v-model="paidDate"
                label="วันที่โอนเงิน"
                :max="new Date()"
                :error-messages="errors" />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              ref="paidTime"
              rules="required"
              name="เวลาที่โอนเงิน">
              <base-time-picker
                v-model="paidTime"
                label="เวลาที่โอนเงิน"
                :max="new Date()"
                :error-messages="errors" />
            </validation-provider>

            <template v-if="slipSrc">
              <v-row
                class="mb-4"
                justify="center">
                <v-col
                  v-for="(item, index) in listSlipSrc"
                  :key="index"
                  cols="auto"
                  class="ml-2"
                  style="position: relative;"
                >
                  <img
                    v-if="isImageSlip(item.slipFile)"
                    :src="item.slipSrc"
                    alt="Upload file"
                    width="100%"
                    height="300px" />
                  <iframe
                    v-else
                    :src="item.slipSrc"
                    width="100%"
                    height="500px">
                  </iframe>
                  <v-icon
                    :color="isImageSlip(item.slipFile) ? 'black' : 'white'"
                    style="position: absolute; top: 15px; right: 15px;"
                    size="24"
                    @click="removeSlipFile(index)"
                  >
                    mdi-close-circle
                  </v-icon>
                </v-col>
              </v-row>
            </template>

            <validation-provider
              v-slot="{ errors }"
              ref="slipFile"
              rules="fileUpload|maximumSizeFile:10"
              name="หลักฐานการโอนเงิน">
              <v-file-input
                filled
                rounded
                multiple
                accept="image/jpg, image/jpeg, image/png, application/pdf"
                placeholder="เลือกรูปถ่ายสลิปโอนเงิน"
                prepend-icon="mdi-camera"
                label="หลักฐานการโอนเงิน"
                :messages="`กรุณาอัพโหลดหลักฐานการชำระที่มีนามสกุลไฟล์เป็น jpg, png, pdf
                  และมีขนาดไฟล์ไม่เกิน 10mb`"
                :value="slipFile"
                :error-messages="errors"
                :clearable="false"
                @change="uploadSlipFile">
              </v-file-input>
            </validation-provider>

            <template v-if="isJuristicCustomer && attachmentSrc">
              <v-row
                class="mb-4"
                justify="center">
                <v-col
                  v-for="(item, index) in listAttachmentSrc"
                  :key="index"
                  cols="auto"
                  class="ml-2"
                  style="position: relative;">
                  <img
                    v-if="isAttachment(item.attachmentFile)"
                    :src="item.attachmentSrc"
                    alt="Upload file"
                    width="100%"
                    height="300px" />
                  <iframe
                    v-else
                    :src="item.attachmentSrc"
                    width="100%"
                    height="500px">
                  </iframe>
                  <v-icon
                    :color="isAttachment(item.attachmentFile) ? 'black' : 'white'"
                    style="position: absolute; top: 15px; right: 15px;"
                    size="24"
                    @click="removeAttachmentFile(index)"
                  >
                    mdi-close-circle
                  </v-icon>
                </v-col>
              </v-row>
            </template>

            <validation-provider
              v-if="isJuristicCustomer"
              v-slot="{ errors }"
              ref="attachmentFile"
              rules="maximumSizeFile:10"
              name="เอกสารแนบ">
              <v-file-input
                filled
                rounded
                multiple
                accept="image/jpg, image/jpeg, image/png, application/pdf"
                placeholder="เลือกเอกสารแนบ"
                prepend-icon="mdi-camera"
                label="เอกสารแนบหัก ณ ที่จ่าย"
                :messages="`กรุณาอัพโหลดหลักฐานการชำระที่มีนามสกุลไฟล์เป็น jpg, png, pdf
                  และมีขนาดไฟล์ไม่เกิน 10mb`"
                :value="attachmentFile"
                :error-messages="errors"
                :clearable="false"
                @change="uploadAttachmentFile">
              </v-file-input>
            </validation-provider>
          </template>
        </validation-observer>
        <div
          v-if="isTransferToBankAccount"
          class="mt-4 error--text text-center font-weight-bold">
          *กรุณาชำระเงินตามยอดในใบเสนอราคาเท่านั้น หากชำระเงินไม่ครบถ้วน จะไม่สามารถเปิดให้บริการได้
        </div>
        <v-row
          v-if="isTransferToBankAccount"
          class="mt-8"
          justify="center">
          <v-btn
            color="primary"
            rounded
            min-width="150"
            @click="submit">
            ยืนยัน
          </v-btn>
        </v-row>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { mapWaitingActions } from 'vue-wait'
import store from '@/store'

const newBasicIdOrderTypes = ['newLineOfficialAccount', 'transferLineOfficialAccount']

export default {
  name: 'UploadSlipPage',
  components: {
    BaseDatePicker: () => import('@/components/BaseDatePicker.vue'),
    BaseTimePicker: () => import('@/components/BaseTimePicker.vue'),
    BaseTextField: () => import('@/components/BaseTextField.vue'),
    PaymentMethod: () => import('@/components/Order/PaymentMethod')
  },
  data() {
    return {
      paymentMethod: '',
      selectOrderId: '',
      paidAmount: undefined,
      paidDate: undefined,
      paidTime: undefined,
      slipFile: undefined,
      slipSrc: undefined,
      listSlipSrc: [],
      attachmentFile: undefined,
      attachmentSrc: undefined,
      listAttachmentSrc: [],
      alert: false,
      showTimePicker: false,
      formReady: false
    }
  },
  computed: {
    ...mapState('Order', ['order', 'orders']),
    mapOrders() {
      return this.orders.map((order) => ({ text: order.code, value: order._id }))
    },
    mapPaymentMethod() {
      return [
        { text: 'โอน/ชำระผ่านบัญชีธนาคาร', value: 'transfer' },
        { text: 'บัตรเครดิต/บัตรเดบิต', value: 'byCreditCard' },
        { text: 'QR Code', value: 'qrCode' }
      ]
    },
    canPaidByCreditCard () {
      return this.order.preVatAmount >= 1000
    },
    isPayViaCreditCard() {
      return this.paymentMethod === 'byCreditCard'
    },
    isTransferToBankAccount() {
      return this.paymentMethod === 'transfer'
    },
    isPayViaQRCode() {
      return this.paymentMethod === 'qrCode'
    },
    isUploadNewLineOfficialAccount () {
      return this.order.type && newBasicIdOrderTypes.includes(this.order.type)
    },
    isJuristicCustomer() {
      return this.order.customerTypeId !== '5'
    },
    paidAt() {
      if (!this.paidDate || !this.paidTime) {
        return undefined
      }

      const timeSplit = this.paidTime.split(':')
      const hour = timeSplit[0]
      const minute = timeSplit[1]

      return this.$utils.createDayjsDate(this.paidDate)
        .set('hour', hour)
        .set('minute', minute)
        .toDate()
    }
  },
  watch: {
    order: {
      deep: true,
      handler(val) {
        if (val.netTotal !== undefined) {
          this.paidAmount = parseFloat(val.netTotal).toFixed(2)
        } else {
          this.paidAmount = 0
        }
      }
    }
  },
  async beforeRouteEnter (to, from, next) {
    const uploadSlipOrderStatus = ['new', 'pending', 'rejected']

    store.dispatch('wait/start', 'app loading', { root: true })
    const orders = await store.dispatch('Order/fetchOrders', {
      status: uploadSlipOrderStatus.toString()
    })

    const { orderId } = to.params
    if (!orderId) {
      next()
    } else {
      const findOrder = orders.find((order) => order._id === orderId)

      if (!findOrder) {
        const order = await store.dispatch('Order/fetchOrderById', orderId)
        const { status, type } = order

        if (['completed', 'paid'].includes(status)) {
          next({ name: 'UploadSlipSuccess' })
        } else if (uploadSlipOrderStatus.includes(status) && newBasicIdOrderTypes.includes(type)) {
          next()
        } else {
          next({ name: 'NotFound' })
        }
      } else {
        next()
      }
    }

    store.dispatch('wait/end', 'app loading', { root: true })
  },
  created() {
    this.$utils.removeKPaymentCheckout()
    this.$utils.removeKPaymentChargeId()
    this.$utils.removeKPaymentUserSaveCard()
  },
  async mounted() {
    if (this.$utils.createDayjsDate().isBetween('2020-12-23', '2021-01-04', null, '[]')) {
      this.$swal({
        title: 'ประกาศ',
        icon: 'warning',
        html: `
          แจ้งหยุดทำการเนื่องในเทศกาลปีใหม่</br>
          <span style="font-weight: bold; color: #01BB00;">ในวันที่ 31 ธันวาคม 2563 ถึงวันที่ 3 มกราคม 2564</span></br>
          <div style="font-weight: 700;">และจะเปิดทำการ วันที่ 4 มกราคม 2564</div></br>
          ทั้งนี้ลูกค้าสามารถทำรายการและแนบหลักฐานการชำระเงินได้ที่เว็บไซต์ไว้ได้ตามปกติ</br>
          เจ้าหน้าที่จะเร่งดำเนินการอนุมัติให้ในวันทำการครับ!</br>
          <div style="font-weight: 700;">หากต้องการติดต่อเจ้าหน้าที่ สามารถติดต่อได้ที่</div>
          <div><span style="font-weight: bold; color: #01BB00;">@linebysellsuki</span></div>
          ขออภัยในความไม่สะดวก</br>
          ท่านที่เดินทาง ขอให้เดินทางโดยสวัสดิภาพ</br> สวัสดีปีใหม่ลูกค้าที่น่ารักทุกท่านครับ! 🥰
        `,
        confirmButtonColor: '#01BB00',
        confirmButtonText: 'ตกลง'
      })
    }

    const { orderId, payment } = this.$route.params

    this.selectOrderId = orderId || ''
    this.paymentMethod = payment || ''

    this.findOrderId().then(() => { this.formReady = true })
    this.hide_alert()
  },
  methods: {
    ...mapActions({
      updateOrderPayments: 'Order/updateOrderPayments',
      updateOrderPaymentsAttachment: 'Order/updateOrderPaymentsAttachment',
      createOrder: 'Payment/createOrder'
    }),
    ...mapWaitingActions('Order', {
      updateOrderPayments: 'app loading',
      updateOrderPaymentsAttachment: 'app loading'
    }),
    async findOrderId() {
      const order = await store.dispatch('Order/fetchOrderById', this.selectOrderId)
      return order
    },
    async changePaymentMethod(val) {
      this.paymentMethod = val
    },
    async selectOrder(val) {
      this.$router.push({ path: `/upload-slip/${val}` })
      await this.findOrderId()
    },
    isImageSlip (slipFile) {
      return Boolean(slipFile && slipFile.name.match(/.(jpg|jpeg|png|gif)$/i))
    },
    uploadSlipFile(file) {
      this.slipFile = file
      if (file.length > 0) {
        this.listSlipSrc = file.map((value) => {
          const objSlipSrc = {}
          if (value.type !== 'application/pdf' && value.type !== 'image/jpeg' && value.type !== 'image/png' && value.type !== 'image/jpg') {
            this.$swal({
              title: 'เกิดข้อผิดพลาด',
              text: 'กรุณาอัพโหลดไฟล์นามสกุล .jpg,png,pdf,jpeg เท่านั้น',
              icon: 'error',
              confirmButtonColor: '#01BB00',
              confirmButtonText: 'ตกลง'
            })
            this.slipFile = []
          } else {
            const reader = new FileReader()
            reader.readAsDataURL(value)
            reader.onload = (e) => {
              this.slipSrc = e.target.result
              objSlipSrc.slipSrc = this.slipSrc
              objSlipSrc.slipFile = value
            }
          }
          return objSlipSrc
        })
      }
    },
    handleChangePaymentMethod(method) {
      const url = new URL(`${window.origin}/upload-slip/${this.$route.params.orderId}/${method}`)
      Object.entries(this.$route.query).forEach(([key, value]) => {
        url.searchParams.append(key, value)
      })

      window.location.href = url.toString()
    },
    removeSlipFile(index) {
      if (Array.isArray(this.slipFile) && this.slipFile.length > index) {
        this.slipFile.splice(index, 1)
      }
      if (Array.isArray(this.listSlipSrc) && this.listSlipSrc.length > index) {
        this.listSlipSrc.splice(index, 1)
      }
    },
    isAttachment(attachmentFile) {
      return Boolean(attachmentFile && attachmentFile.name.match(/.(jpg|jpeg|png|gif)$/i))
    },
    uploadAttachmentFile(file) {
      this.attachmentFile = file

      if (file.length > 0) {
        this.listAttachmentSrc = file.map((value) => {
          const objAttachmentSrc = {}
          const reader = new FileReader()
          reader.readAsDataURL(value)
          reader.onload = (e) => {
            this.attachmentSrc = e.target.result
            objAttachmentSrc.attachmentSrc = this.attachmentSrc
            objAttachmentSrc.attachmentFile = value
          }
          return objAttachmentSrc
        })
      }
    },
    removeAttachmentFile(index) {
      if (Array.isArray(this.attachmentFile) && this.attachmentFile.length > index) {
        this.attachmentFile.splice(index, 1)
      }
      if (Array.isArray(this.listAttachmentSrc) && this.listAttachmentSrc.length > index) {
        this.listAttachmentSrc.splice(index, 1)
      }
    },
    async submit() {
      const canSubmit = await this.$refs.uploadSlipForm.validate()
      if (!canSubmit) {
        this.$utils.scrollToErrorField('uploadSlipForm', this)
        return
      }

      const bodyData = {
        type: 'slip',
        paidAt: this.paidAt,
        paidAmount: +this.paidAmount,
        totalImage: this.slipFile.length
      }

      const formData = new FormData()
      if (this.slipFile.length > 0) {
        this.slipFile.forEach((value) => {
          formData.append('file', value)
        })

        formData.append('bodyData', JSON.stringify(bodyData))

        this.updateOrderPayments({ id: this.selectOrderId, params: formData }).then((response) => {
          if (this.isJuristicCustomer && this.attachmentFile !== undefined) {
            const bodyAttachData = { type: 'attachment', totalImage: this.attachmentFile.length }
            this.uploadFile(bodyAttachData, this.attachmentFile)
          }

          this.$router.push({ name: 'UploadSlipSuccess',
            params: {
              order: response
            } })
        })
      }
    },
    async uploadFile(bodyData, file) {
      const formData = new FormData()
      if (file.length > 0) {
        file.forEach((value) => {
          formData.append('file', value)
        })

        formData.append('bodyData', JSON.stringify(bodyData))

        this.updateOrderPayments({ id: this.selectOrderId, params: formData })
      }
    },
    hide_alert () {
      window.setInterval(() => {
        this.alert = false
      }, 5000)
    }
  }
}
</script>
<style lang="scss">
.HintTextColor{
  .v-messages__message{
    color: red;
  }
}
.all-rights-reserved {
  color: red;
  font-size: 12px;
}
</style>
